/**
 * LeaveRequestsPanel — Phase-3b leave workflow surface.
 *
 * Sits below the staff-attendance list on the Attendance page. Two
 * views:
 *   * Pending list — admins approve/reject; submitters cancel their
 *     own requests.
 *   * Submit dialog — self-service leave request, or admin-on-behalf
 *     when the staff member has no app login (or admin wants to file
 *     for someone).
 *
 * The panel shows a compact leave balance strip for the CURRENT user
 * so they can eyeball remaining days before submitting.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Umbrella, Plus, Check, X, CalendarBlank, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";


const STATUS_STYLES = {
  submitted: "bg-amber-100 text-amber-900 border-amber-200",
  approved:  "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected:  "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};

const fmtRange = (a, b) => {
  const f = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return a === b ? f(a) : `${f(a)} → ${f(b)}`;
};


export default function LeaveRequestsPanel({ roster, isAdmin }) {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [balances, setBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showSubmit, setShowSubmit] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, b, t] = await Promise.all([
        api.get("/leaves", { params: { status: "submitted" } }),
        api.get("/leaves/balance"),
        // Leave types is admin-gated on backend; non-admins can still
        // submit but need types loaded via balance. So we fall back to
        // deriving types from balance when the fetch 403s.
        api.get("/settings/leave-types").catch(() => ({ data: [] })),
      ]);
      setPending(p.data || []);
      setBalances(b.data?.balances || []);
      const types = (t.data || []).filter(x => x.active !== false);
      // If the /settings/leave-types call was blocked (reception, therapist),
      // fall back to leave-type ids exposed on the balance payload.
      const fallback = (b.data?.balances || []).map(x => ({
        id: x.leave_type_id, name: x.leave_type_name, code: x.leave_type_code,
        is_paid: x.is_paid, color: x.color, active: true,
      }));
      setLeaveTypes(types.length ? types : fallback);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (leave, action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Reason for rejection:") || "";
      if (!reason.trim()) return;
    }
    try {
      await api.post(`/leaves/${leave.id}/${action}`,
        action === "cancel" ? {} : { reason });
      toast.success(`Leave ${action}${action.endsWith("e") ? "d" : "ed"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card data-testid="leave-requests-panel">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <Umbrella size={16} weight="duotone" className="text-primary" />
            Leave requests
            {pending.length > 0 && (
              <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-[10px]">{pending.length} pending</Badge>
            )}
          </CardTitle>
          <Button size="sm" onClick={() => setShowSubmit(true)} className="gap-1.5" data-testid="submit-leave-btn">
            <Plus size={13} weight="bold" /> Request leave
          </Button>
        </div>
        {/* Balance strip for the current user */}
        {balances.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="leave-balance-strip">
            {balances.map(b => (
              <Badge
                key={b.leave_type_id}
                variant="outline"
                className="text-[10px] gap-1"
                style={{ borderColor: `${b.color || "#6B7280"}66`, color: b.color || undefined }}
                data-testid={`leave-balance-${b.leave_type_code}`}
                title={`Entitled ${b.entitled} · Approved ${b.approved} · Pending ${b.pending}`}
              >
                {b.leave_type_name}: <span className="font-semibold">{b.remaining}</span>/{b.entitled}
              </Badge>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="p-0">
        {loading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
        {!loading && pending.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="leave-empty">
            No pending leave requests.
          </div>
        )}
        {!loading && pending.length > 0 && (
          <div className="divide-y divide-border">
            {pending.map(l => (
              <div key={l.id} className="px-4 sm:px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`leave-row-${l.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm font-medium">
                    {l.user_name}
                    {l.submitted_on_behalf && <span className="ml-1 text-[10px] text-muted-foreground">(on behalf)</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {l.leave_type_name} · {fmtRange(l.from_date, l.to_date)} · {l.days} day{l.days === 1 ? "" : "s"}
                    {l.is_half_day && ` · ${l.half === "first" ? "1st half" : "2nd half"}`}
                  </div>
                  {l.reason && (
                    <div className="text-[10px] text-muted-foreground italic truncate max-w-md">&ldquo;{l.reason}&rdquo;</div>
                  )}
                </div>
                <Badge className={`text-[10px] ${STATUS_STYLES[l.status] || STATUS_STYLES.submitted}`}>{l.status}</Badge>
                {isAdmin && l.status === "submitted" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => decide(l, "approve")} className="gap-1 text-emerald-700" data-testid={`leave-approve-${l.id}`}>
                      <Check size={13} weight="bold" /> Approve
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => decide(l, "reject")} className="gap-1 text-red-700" data-testid={`leave-reject-${l.id}`}>
                      <X size={13} weight="bold" /> Reject
                    </Button>
                  </>
                )}
                {!isAdmin && l.user_id === user?.id && l.status === "submitted" && (
                  <Button size="sm" variant="ghost" onClick={() => decide(l, "cancel")} className="text-xs" data-testid={`leave-cancel-${l.id}`}>
                    Cancel
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {showSubmit && (
        <SubmitLeaveDialog
          open={showSubmit}
          onClose={() => setShowSubmit(false)}
          onSubmitted={() => { setShowSubmit(false); load(); }}
          leaveTypes={leaveTypes}
          roster={roster || []}
          isAdmin={isAdmin}
          balances={balances}
        />
      )}
    </Card>
  );
}


function SubmitLeaveDialog({ open, onClose, onSubmitted, leaveTypes, roster, isAdmin, balances }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    user_id: "",   // "" = self
    leave_type_id: leaveTypes[0]?.id || "",
    from_date: today,
    to_date: today,
    is_half_day: false,
    half: "first",
    reason: "",
  });
  const [saving, setSaving] = useState(false);

  // Filter roster to people the current admin can submit for.
  // Non-admins see only "myself".
  const targets = [
    { id: "", name: "Myself" },
    ...(isAdmin ? roster.filter(r => (r.roles || []).every(x => x !== "parent" && x !== "guardian")).map(r => ({ id: r.id, name: r.name })) : []),
  ];

  const activeType = leaveTypes.find(t => t.id === form.leave_type_id);
  const activeBalance = balances.find(b => b.leave_type_id === form.leave_type_id);

  const submit = async () => {
    if (!form.leave_type_id) { toast.error("Pick a leave type"); return; }
    if (!form.from_date || !form.to_date) { toast.error("Pick dates"); return; }
    if (form.to_date < form.from_date) { toast.error("End date can't be before start date"); return; }
    if (form.is_half_day && form.from_date !== form.to_date) {
      toast.error("Half-day leave must be a single date");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        leave_type_id: form.leave_type_id,
        from_date: form.from_date,
        to_date: form.to_date,
        is_half_day: form.is_half_day,
        half: form.is_half_day ? form.half : null,
        reason: form.reason,
      };
      if (form.user_id) payload.user_id = form.user_id;
      await api.post("/leaves", payload);
      toast.success("Leave request submitted");
      onSubmitted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-card sm:max-w-lg" data-testid="submit-leave-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarBlank size={16} weight="duotone" className="text-primary" /> Request leave
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isAdmin ? "Submit for yourself, or pick another staff member to file on their behalf." : "Submit a leave request for yourself."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {isAdmin && (
            <div className="space-y-1.5">
              <Label>For</Label>
              <Select value={form.user_id || "__self__"} onValueChange={v => setForm(f => ({ ...f, user_id: v === "__self__" ? "" : v }))}>
                <SelectTrigger data-testid="leave-target"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="__self__">Myself</SelectItem>
                  {targets.slice(1).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Leave type</Label>
            <Select value={form.leave_type_id} onValueChange={v => setForm(f => ({ ...f, leave_type_id: v }))}>
              <SelectTrigger data-testid="leave-type"><SelectValue placeholder="Pick a type" /></SelectTrigger>
              <SelectContent>
                {leaveTypes.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full inline-block" style={{ background: t.color || "#6B7280" }} />
                      {t.name}
                      {!t.is_paid && <Badge className="ml-1 text-[9px] bg-red-100 text-red-800 border-red-200">Unpaid</Badge>}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeBalance && (
              <p className="text-[10px] text-muted-foreground">
                Available: <span className="font-medium">{activeBalance.remaining}</span> / {activeBalance.entitled} day
                {activeBalance.entitled === 1 ? "" : "s"} this year
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>From</Label>
              <Input
                type="date" value={form.from_date}
                onChange={e => setForm(f => ({ ...f, from_date: e.target.value, ...(f.is_half_day ? { to_date: e.target.value } : {}) }))}
                data-testid="leave-from"
              />
            </div>
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input
                type="date" value={form.to_date}
                min={form.from_date}
                disabled={form.is_half_day}
                onChange={e => setForm(f => ({ ...f, to_date: e.target.value }))}
                data-testid="leave-to"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <Checkbox
              checked={form.is_half_day}
              onCheckedChange={v => setForm(f => ({ ...f, is_half_day: !!v, to_date: v ? f.from_date : f.to_date }))}
              data-testid="leave-halfday"
            />
            Half-day leave
          </label>
          {form.is_half_day && (
            <div className="space-y-1.5 pl-6">
              <Label>Which half</Label>
              <Select value={form.half} onValueChange={v => setForm(f => ({ ...f, half: v }))}>
                <SelectTrigger data-testid="leave-half"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="first">First half (morning)</SelectItem>
                  <SelectItem value="second">Second half (afternoon)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Input
              value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="Optional — helps the approver decide"
              data-testid="leave-reason"
            />
          </div>

          {activeType && !activeType.is_paid && (
            <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-[11px] px-3 py-2 flex items-start gap-2">
              <Warning size={12} className="mt-0.5" />
              <span>This is an <span className="font-semibold">unpaid</span> leave — days approved will feed into payslip deductions.</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="gap-1" data-testid="submit-leave-cancel">
            <X size={13} /> Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-leave-save">
            {saving ? "Submitting…" : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
